import React, { useState, useRef } from 'react';
import { X, Plus, Trash2, Loader2, Upload } from 'lucide-react';
import adminAxios from './adminApi';
import { resolveImageUrl } from '../../utils/imageUrl';

const blankTier = { quantity: 1, price: 0 };
const blankProduct = {
  name: '',
  full_name: '',
  weight: '',
  description: '',
  image_url: '',
  category: 'Refrigerants',
  in_stock: true,
  price_tiers: [{ quantity: 1, price: 0 }, { quantity: 10, price: 0 }, { quantity: 20, price: 0 }, { quantity: 40, price: 0 }],
};

const ProductEditModal = ({ product, onClose, onSaved }) => {
  const isNew = !product?.id;
  const [form, setForm] = useState(product ? { ...product } : { ...blankProduct });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await adminAxios.post('/admin/products/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setField('image_url', data.image_url);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Image upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const setTier = (idx, key, val) => {
    setForm((f) => {
      const tiers = [...f.price_tiers];
      tiers[idx] = { ...tiers[idx], [key]: key === 'price' ? parseFloat(val) || 0 : parseInt(val) || 0 };
      return { ...f, price_tiers: tiers };
    });
  };

  const addTier = () => setForm((f) => ({ ...f, price_tiers: [...f.price_tiers, { ...blankTier }] }));
  const removeTier = (idx) => setForm((f) => ({ ...f, price_tiers: f.price_tiers.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        full_name: form.full_name,
        weight: form.weight,
        description: form.description,
        image_url: form.image_url || null,
        category: form.category || 'Refrigerants',
        in_stock: !!form.in_stock,
        price_tiers: form.price_tiers
          .filter((t) => t.quantity > 0)
          .map((t) => ({ quantity: Number(t.quantity), price: Number(t.price) }))
          .sort((a, b) => a.quantity - b.quantity),
      };
      if (payload.price_tiers.length === 0) {
        throw new Error('At least one price tier is required');
      }
      if (isNew) {
        await adminAxios.post('/admin/products', payload);
      } else {
        await adminAxios.put(`/admin/products/${product.id}`, payload);
      }
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" data-testid="product-edit-modal">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-bold">{isNew ? 'Add Product' : `Edit ${product.full_name}`}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="product-modal-close">
            <X size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Short name *</label>
              <input required value={form.name} onChange={(e) => setField('name', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="R410A" data-testid="product-name-input" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Weight *</label>
              <input required value={form.weight} onChange={(e) => setField('weight', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="25lb" data-testid="product-weight-input" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Full name *</label>
            <input required value={form.full_name} onChange={(e) => setField('full_name', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="R410A Refrigerant" data-testid="product-fullname-input" />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Description *</label>
            <textarea required rows={3} value={form.description} onChange={(e) => setField('description', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" data-testid="product-description-input" />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Product image</label>
            <div className="flex gap-2 mb-2">
              <input
                value={form.image_url || ''}
                onChange={(e) => setField('image_url', e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                placeholder="Paste a URL or upload →"
                data-testid="product-image-input"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleFilePick}
                className="hidden"
                data-testid="product-image-file"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-3 py-2 rounded-lg text-sm font-semibold"
                data-testid="product-image-upload-btn"
              >
                {uploading ? <><Loader2 className="animate-spin" size={14} /> Uploading...</> : <><Upload size={14} /> Upload</>}
              </button>
            </div>
            <p className="text-xs text-gray-400">JPG / PNG / WEBP / GIF · max 5 MB</p>
            {form.image_url && (
              <img src={resolveImageUrl(form.image_url)} alt="preview" className="mt-2 h-20 w-20 object-cover rounded border" />
            )}
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="in_stock" checked={!!form.in_stock} onChange={(e) => setField('in_stock', e.target.checked)} data-testid="product-instock-input" />
            <label htmlFor="in_stock" className="text-sm font-semibold">In stock</label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold">Volume Price Tiers</label>
              <button type="button" onClick={addTier} className="text-blue-600 text-sm font-semibold flex items-center gap-1">
                <Plus size={14} /> Add tier
              </button>
            </div>
            <div className="space-y-2">
              {form.price_tiers.map((tier, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                  <div>
                    <input type="number" min="1" value={tier.quantity} onChange={(e) => setTier(idx, 'quantity', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="Qty (e.g. 10)" />
                  </div>
                  <div>
                    <input type="number" min="0" step="0.01" value={tier.price} onChange={(e) => setTier(idx, 'price', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="Price per unit" />
                  </div>
                  <button type="button" onClick={() => removeTier(idx)} className="text-red-500 hover:text-red-700 p-1">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="product-modal-error">{error}</div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-3 rounded-lg font-semibold transition" disabled={saving}>
              Cancel
            </button>
            <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2" data-testid="product-modal-save">
              {saving ? <><Loader2 className="animate-spin" size={16} /> Saving...</> : (isNew ? 'Create product' : 'Save changes')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductEditModal;
